/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from "./Attributes";

class Section {
    public name: string;
    public desc: string;
    public depth: number;
    public minLevel: number;
    public children: Section[];
    public parentName: string | null;
    public options: string[];
    public selectedOption: string;
    public exportSection: boolean = true;
    public fnDisplaySection: ((a: Map<string, string[]>) => Section | null) | null = null;
    public fnExportSection: ((a: Map<string, string[]>, da: string[]) => void) | null = null;

    constructor(name: string,
                desc: string,
                depth: number = 0,
                minLevel: number = 0,
                children: Section[] = [],
                parent: Section | null = null,
                options: string[] = [],
                selectedOption: string = ''
    ) {
      this.name = name;
      this.desc = desc;
      this.depth = depth;
      this.minLevel = minLevel;
      this.children = children;
      this.parentName = parent?.name || null;
      this.options = options;
      this.selectedOption = selectedOption;
    }

    static removeAbilitiesThatHaveBeenHandled = (sections: Section[]): Section[] => {
      const handledItems = [
        { name: />? *Spellcasting( Ability)?.*/i, desc: null },
        { name: />? *Cantrips.*/i, desc: null },
        { name: />? *Eldritch Prankster Spells \(table\)/i, desc: null },
        { name: />? *Arcane Warrior Spells \(table\)/i, desc: null },
        { name: />? *Spell Slots.*/i, desc: null },
        { name: />? *Spells Known.*/i, desc: null },
        { name: />? *Ritual Casting.*/i, desc: null },
        { name: />? *(Your )?Spellbook.*/i, desc: null },
        { name: />? *Your Pact Boon.*/i, desc: null },
        { name: />? *Learning Spells of 1st Level and Higher.*/i, desc: null },
        { name: />? *Preparing and Casting Spells.*/i, desc: null },
        { name: null, desc: /^\>?\*\*_?Spellcasting Ability._?\*\* .*/i },
        { name: null, desc: /^\>?\*\*_?Spell save DC_?\*\* = .*/i },
        { name: null, desc: /^\>?\*\*_?Spell attack modifier_?\*\* = .*/i },
        { name: null, desc: /^\>?\*\*_?Cantrips.?_?\*\*.*/i },
        { name: null, desc: /^\>?\*\*_?Spell Slots.?_?\*\*.*/i },
        { name: null, desc: /^\>?\*\*_?1st-Level and Higher Spells Known\._?\*\*.*/i }
      ];
      return sections.filter(ability => !handledItems.some(item =>
        (item.name == null || (item.name as RegExp).test(ability.name)) &&
        (item.desc == null || (item.desc as RegExp).test(ability.desc))
      ));
    }

    static isBonusAction = (section: Section) => {
      return (section.desc.toLowerCase().includes(' use a bonus action') || 
              section.desc.toLowerCase().includes('as a bonus action') ||
              section.desc.toLowerCase().includes(' use your bonus action') ||
              section.name.toLowerCase() == 'cunning action');
    }
    static isReaction = (section: Section) => {
      return (section.desc.toLowerCase().includes('you can use your reaction') ||
              section.desc.toLowerCase().includes('you can use a reaction'));
    }
    static isSpell = (section: Section) => {
      return section.desc.toLowerCase().includes('sorcery point') ||
             section.name.toLowerCase().startsWith('eldritch invocations');
    }

    static simplifySections = (sections: Section[]) => {
      sections.forEach(section => {
        Section.simplifySection(section);
      });
    }

    static simplifySection = (section: {name: string, desc: string}) => {
      if (section.name === '' && section.desc.startsWith('>**Spell ')) {
        for (const attr of Attributes.attributes) {
          if (section.desc.toLowerCase() === `>**Spell save DC** = 8 + your proficiency bonus + your ${attr.toLowerCase()} modifier`.toLowerCase()) {
            section.desc = `**Spell save DC** = [${attr.toLowerCase().substring(0, 3)} SAVE] (8 + prof + ${attr.toUpperCase().substring(0, 3)})`;
          }
          if (section.desc.toLowerCase() === `>**Spell attack modifier** = your proficiency bonus + your ${attr.toLowerCase()} modifier`.toLowerCase()) {
            section.desc = `**Spell attack modifier** = [${attr.toLowerCase().substring(0, 3)} ATK] (prof + ${attr.toUpperCase().substring(0, 3)})`;
          }
        }
      }
      if (section.name.endsWith('.')) {
        section.name = section.name.substring(0, section.name.length-1);
      }
    }

    static convertSpellsToTable(markdown: string): string {
      // Split the input into lines
      const lines = markdown.split('\n');
      
      // Define the regex to capture levels, slots, and spells
      const regex = /\* (Cantrips|(\d+)[a-z]{2})[ -](level )?\(((\d+) (slots?)|at will)\):(.*)/i;
    
      // Initialize an array to hold the table rows
      const normalRowsPre: string[] = [];
      const normalRowsPost: string[] = [];
      const tableRows: string[] = [];
    
      let currentTableRow = null;
      // Loop over each line to extract the necessary details
      for (const line of lines) {
        const match = line.match(regex);
        if (match) {
          const level = match[1];  // "Cantrips" or the level number (e.g., 1st, 2nd, etc.)
          const slots = level.toLowerCase() === 'cantrips' ? '--' : match[5];  // Cantrips have no slots
          const spells = match[7].trim().replace(/_/g, "");  // The list of spells
          currentTableRow = `|${level}|${slots}|_${spells}`;
          tableRows.push(currentTableRow + '_|');
        } else {
          if (currentTableRow && !line.includes('*')) {
            if (line) {
              currentTableRow += ', ' + line;
            }
            tableRows.pop();
            tableRows.push(currentTableRow + '_|');
          } else {
            if (tableRows.length === 0) {
              normalRowsPre.push(line);
            } else {
              normalRowsPost.push(line);
            }
          }
        }
      }
      if (tableRows.length === 0) {
        return markdown;
      }
    
      // Create the table header and return the full table as a string
      const tableHeader = '\n\n\n|level|slots|spells|\n|-----|-----|------|';
      return `${normalRowsPre.join('\n')}\n${tableHeader}\n${tableRows.join('\n')}\n${normalRowsPost.join('\n')}`;
    }
  }
  
  export default Section;